import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import api from 'services/api.service';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { TMaintenance } from '@timeedit/types';
import type { TAppThunk, TRootState } from './store';

type TMaintenanceState = IDefaultReducerState & {
  maintenance: TMaintenance[];
  messageMaintenanceResponse: string;
};

export const initialState: TMaintenanceState = {
  loading: false,
  hasErrors: false,
  maintenance: [],
  messageMaintenanceResponse: '',
};

// Slice
const slice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    fetchMaintenanceRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchMaintenanceSuccess: (
      state,
      { payload }: PayloadAction<TMaintenance[]>
    ) => {
      state.maintenance = payload;
      finishedLoadingSuccess(state);
    },
    fetchMaintenanceFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateMaintenanceRequest: (state) => {
      isLoadingRequest(state);
    },
    updateMaintenanceFailure: (state) => {
      finishedLoadingFailure(state);
    },
    messageMaintenanceSuccess: (state, { payload }: PayloadAction<string>) => {
      state.messageMaintenanceResponse = payload;
    },
    updateMessageMaintenanceSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const maintenanceLoading = (state: TRootState) =>
  state.maintenance.loading;
export const messageMaintenanceResponse = (state: TRootState) =>
  state.maintenance.messageMaintenanceResponse;
export const maintenanceSelector = (state: TRootState) =>
  state.maintenance.maintenance;

// Actions
export const {
  fetchMaintenanceRequest,
  fetchMaintenanceSuccess,
  fetchMaintenanceFailure,
  updateMaintenanceRequest,
  updateMaintenanceFailure,
  messageMaintenanceSuccess,
  updateMessageMaintenanceSuccess,
} = slice.actions;

export const fetchMaintenance = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchMaintenanceRequest());
    const maintenance = await api.get({
      endpoint: `/system-maintenance`,
    });
    dispatch(fetchMaintenanceSuccess(maintenance.results));
  } catch (e) {
    dispatch(fetchMaintenanceFailure());
    return console.error(e);
  }
};

export const MAINTENANCE_CLEAR_CACHE_ID = 'maintenance-clear-cache';

export const runMaintenance =
  (maintenanceItemId: string): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateMaintenanceRequest());

      const response = await maintenanceRequest(maintenanceItemId);
      dispatch(messageMaintenanceSuccess(response.message ?? ''));

      dispatch(updateMessageMaintenanceSuccess());
    } catch (e) {
      dispatch(updateMaintenanceFailure());
      return console.error(e);
    }
  };

type TMaintenanceResponse = {
  message?: string;
};

const maintenanceRequest = async (maintenanceItemId: string) =>
  maintenanceItemId === MAINTENANCE_CLEAR_CACHE_ID
    ? api.post<void, TMaintenanceResponse>({
        endpoint: `/integration/maintenance/clear-cache`,
      })
    : api.patch<void, TMaintenanceResponse>({
        endpoint: `/system-maintenance/${maintenanceItemId}`,
      });
